import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Save, Trash2, X, Edit } from 'lucide-react';
import { catalogService } from '../services/catalogService';
import { categoryService } from '../services/categoryService';
import { productService } from '../services/productService';
import { parseErrorMessage } from '../utils/helpers';

const EMPTY_LIST = { name: '', currency: 'USD', description: '', is_active: true };
const EMPTY_RULE = {
  apply_to: 'CATEGORY',
  product: '',
  category: '',
  price_type: 'DISCOUNT',
  discount_percent: '20',
  formula: '',
  fixed_price: '',
  min_quantity: '0',
  valid_from: '',
  valid_to: '',
  is_active: true,
};

const PRICE_TYPE_LABEL = {
  DISCOUNT: 'Descuento',
  FORMULA: 'Fórmula',
  FIXED: 'Precio fijo',
};

const toArr = (data) => (Array.isArray(data) ? data : data?.results || data?.items || data?.data || []);

const DiscountManager = () => {
  const [lists, setLists] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [rules, setRules] = useState([]);
  const [listForm, setListForm] = useState(EMPTY_LIST);
  const [editingListId, setEditingListId] = useState(null);
  const [showListForm, setShowListForm] = useState(false);
  const [ruleForm, setRuleForm] = useState(EMPTY_RULE);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  const selectedList = lists.find((l) => l.id === selectedId) || null;

  const loadLists = useCallback(async () => {
    try {
      const data = await catalogService.discountLists.getAll();
      setLists(toArr(data));
    } catch (err) {
      console.error('Error cargando listas de descuento:', err);
      setLists([]);
    }
  }, []);

  const loadRules = useCallback(async (listId) => {
    if (!listId) { setRules([]); return; }
    try {
      const data = await catalogService.discountLists.getRules(listId);
      setRules(toArr(data));
    } catch (err) {
      console.error('Error cargando reglas:', err);
      setRules([]);
    }
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);

  useEffect(() => {
    loadRules(selectedId);
  }, [selectedId, loadRules]);

  useEffect(() => {
    categoryService.getFlat({ active_only: true })
      .then((data) => setCategories(toArr(data)))
      .catch(() => setCategories([]));
    productService.getAll()
      .then((data) => setProducts(toArr(data)))
      .catch(() => setProducts([]));
  }, []);

  const openNewList = () => {
    setEditingListId(null);
    setListForm(EMPTY_LIST);
    setShowListForm(true);
  };

  const openEditList = (list) => {
    setEditingListId(list.id);
    setListForm({
      name: list.name || '',
      currency: list.currency || 'USD',
      description: list.description || '',
      is_active: list.is_active !== false,
    });
    setShowListForm(true);
  };

  const saveList = async (e) => {
    e.preventDefault();
    if (!listForm.name.trim()) {
      alert('El nombre de la lista es obligatorio.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...listForm, name: listForm.name.trim() };
      const saved = editingListId
        ? await catalogService.discountLists.update(editingListId, payload)
        : await catalogService.discountLists.create(payload);
      await loadLists();
      setShowListForm(false);
      setSelectedId(saved?.id || editingListId);
    } catch (err) {
      alert(parseErrorMessage(err, 'No se pudo guardar la lista de descuento.'));
    } finally {
      setSaving(false);
    }
  };

  const deleteList = async (list) => {
    if (!window.confirm(`¿Eliminar la lista "${list.name}" y sus reglas?`)) return;
    try {
      await catalogService.discountLists.delete(list.id);
      if (selectedId === list.id) setSelectedId(null);
      await loadLists();
    } catch (err) {
      alert(parseErrorMessage(err, 'No se pudo eliminar la lista.'));
    }
  };

  const openNewRule = () => {
    setEditingRuleId(null);
    setRuleForm(EMPTY_RULE);
    setShowRuleForm(true);
  };

  const openEditRule = (rule) => {
    setEditingRuleId(rule.id);
    setRuleForm({
      apply_to: rule.apply_to || 'CATEGORY',
      product: rule.product || '',
      category: rule.category || '',
      price_type: rule.price_type || 'DISCOUNT',
      discount_percent: rule.discount_percent ?? '',
      formula: rule.formula || '',
      fixed_price: rule.fixed_price ?? '',
      min_quantity: rule.min_quantity ?? '0',
      valid_from: rule.valid_from || '',
      valid_to: rule.valid_to || '',
      is_active: rule.is_active !== false,
    });
    setShowRuleForm(true);
  };

  const saveRule = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    setSaving(true);
    try {
      const payload = {
        apply_to: ruleForm.apply_to,
        product: ruleForm.apply_to === 'PRODUCT' && ruleForm.product ? parseInt(ruleForm.product, 10) : null,
        category: ruleForm.apply_to === 'CATEGORY' && ruleForm.category ? parseInt(ruleForm.category, 10) : null,
        price_type: ruleForm.price_type,
        discount_percent: ruleForm.price_type === 'DISCOUNT' && ruleForm.discount_percent !== ''
          ? parseFloat(ruleForm.discount_percent) : null,
        formula: ruleForm.price_type === 'FORMULA' ? (ruleForm.formula || null) : null,
        fixed_price: ruleForm.price_type === 'FIXED' && ruleForm.fixed_price !== ''
          ? parseFloat(ruleForm.fixed_price) : null,
        min_quantity: ruleForm.min_quantity !== '' ? parseFloat(ruleForm.min_quantity) : 0,
        valid_from: ruleForm.valid_from || null,
        valid_to: ruleForm.valid_to || null,
        is_active: !!ruleForm.is_active,
      };
      if (editingRuleId) {
        await catalogService.discountLists.updateRule(selectedId, editingRuleId, payload);
      } else {
        await catalogService.discountLists.addRule(selectedId, payload);
      }
      await loadRules(selectedId);
      await loadLists();
      setShowRuleForm(false);
    } catch (err) {
      alert(parseErrorMessage(err, 'No se pudo guardar la regla.'));
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm('¿Eliminar esta regla de descuento?')) return;
    try {
      await catalogService.discountLists.deleteRule(selectedId, rule.id);
      await loadRules(selectedId);
      await loadLists();
    } catch (err) {
      alert(parseErrorMessage(err, 'No se pudo eliminar la regla.'));
    }
  };

  const setRule = (key, value) => setRuleForm((prev) => ({ ...prev, [key]: value }));

  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500';
  const radioCls = 'flex items-center gap-2 text-sm text-gray-700';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Listas de descuento</h2>
          <p className="text-sm text-gray-500 mt-1">
            Creá listas y reglas (producto o categoría). Los contactos eligen una lista en el campo Descuento.
          </p>
        </div>
        <button
          type="button"
          onClick={openNewList}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Nueva lista
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase">
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Moneda</th>
              <th className="px-4 py-3">Reglas</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 w-28" />
            </tr>
          </thead>
          <tbody>
            {lists.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                  No hay listas de descuento. Creá la primera para usarla en contactos.
                </td>
              </tr>
            )}
            {lists.map((list) => (
              <tr
                key={list.id}
                onClick={() => setSelectedId(list.id)}
                className={`border-b border-gray-100 cursor-pointer ${selectedId === list.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
              >
                <td className="px-4 py-3 text-sm font-medium text-gray-800">{list.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{list.currency}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{list.rules_count ?? list.rules?.length ?? 0}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${list.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {list.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <button type="button" onClick={() => openEditList(list)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Editar">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => deleteList(list)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedList && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-800">
              Reglas de {selectedList.name}
            </h3>
            <button
              type="button"
              onClick={openNewRule}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Nueva regla
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">
                  <th className="px-3 py-2">Aplicar a</th>
                  <th className="px-3 py-2">Destino</th>
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2">Valor</th>
                  <th className="px-3 py-2">Cant. mín.</th>
                  <th className="px-3 py-2">Validez</th>
                  <th className="px-3 py-2 w-24" />
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-400">
                      Esta lista no tiene reglas. Agregá una para definir el descuento.
                    </td>
                  </tr>
                )}
                {rules.map((rule) => {
                  const dest = rule.apply_to === 'PRODUCT' ? (rule.product_name || 'Producto') : (rule.category_name || 'Categoría');
                  const value = rule.price_type === 'DISCOUNT'
                    ? `${rule.discount_percent ?? 0}%`
                    : rule.price_type === 'FIXED'
                      ? rule.fixed_price
                      : (rule.formula || '—');
                  const validity = [rule.valid_from, rule.valid_to].filter(Boolean).join(' → ') || 'Sin límite';
                  return (
                    <tr key={rule.id} className="border-b border-gray-100 text-sm">
                      <td className="px-3 py-2">{rule.apply_to === 'PRODUCT' ? 'Producto' : 'Categoría'}</td>
                      <td className="px-3 py-2">{dest}</td>
                      <td className="px-3 py-2">{PRICE_TYPE_LABEL[rule.price_type] || rule.price_type}</td>
                      <td className="px-3 py-2">{value}</td>
                      <td className="px-3 py-2">{rule.min_quantity ?? 0}</td>
                      <td className="px-3 py-2 text-gray-500">{validity}</td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={() => openEditRule(rule)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => deleteRule(rule)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showListForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={saveList} className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">{editingListId ? 'Editar lista' : 'Nueva lista de descuento'}</h3>
              <button type="button" onClick={() => setShowListForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input className={inputCls} value={listForm.name} onChange={(e) => setListForm((p) => ({ ...p, name: e.target.value }))} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
              <select className={inputCls} value={listForm.currency} onChange={(e) => setListForm((p) => ({ ...p, currency: e.target.value }))}>
                <option value="USD">USD</option>
                <option value="ARS">ARS</option>
                <option value="EUR">EUR</option>
                <option value="CNY">CNY</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea className={inputCls} rows={2} value={listForm.description} onChange={(e) => setListForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={listForm.is_active} onChange={(e) => setListForm((p) => ({ ...p, is_active: e.target.checked }))} />
              Activa
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowListForm(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700">Descartar</button>
              <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-60">
                <Save className="w-4 h-4" />
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showRuleForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={saveRule} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">
                {editingRuleId ? 'Editar regla' : 'Abrir: Reglas de lista de descuentos'}
              </h3>
              <button type="button" onClick={() => setShowRuleForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Aplicar a</p>
              <div className="flex gap-6">
                <label className={radioCls}>
                  <input type="radio" name="apply_to" checked={ruleForm.apply_to === 'PRODUCT'} onChange={() => setRule('apply_to', 'PRODUCT')} />
                  Producto
                </label>
                <label className={radioCls}>
                  <input type="radio" name="apply_to" checked={ruleForm.apply_to === 'CATEGORY'} onChange={() => setRule('apply_to', 'CATEGORY')} />
                  Categoría
                </label>
              </div>
            </div>

            {ruleForm.apply_to === 'PRODUCT' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Producto</label>
                <select className={inputCls} value={ruleForm.product} onChange={(e) => setRule('product', e.target.value)} required>
                  <option value="">Seleccionar</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.product_code ? ` (${p.product_code})` : ''}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                <select className={inputCls} value={ruleForm.category} onChange={(e) => setRule('category', e.target.value)} required>
                  <option value="">Seleccionar</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Tipo de precio</p>
              <div className="flex flex-wrap gap-6">
                <label className={radioCls}>
                  <input type="radio" name="price_type" checked={ruleForm.price_type === 'DISCOUNT'} onChange={() => setRule('price_type', 'DISCOUNT')} />
                  Descuento
                </label>
                <label className={radioCls}>
                  <input type="radio" name="price_type" checked={ruleForm.price_type === 'FORMULA'} onChange={() => setRule('price_type', 'FORMULA')} />
                  Fórmula
                </label>
                <label className={radioCls}>
                  <input type="radio" name="price_type" checked={ruleForm.price_type === 'FIXED'} onChange={() => setRule('price_type', 'FIXED')} />
                  Precio fijo
                </label>
              </div>
            </div>

            {ruleForm.price_type === 'DISCOUNT' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descuento</label>
                <div className="flex items-center gap-2">
                  <input type="number" step="0.01" min="0" max="100" className={`${inputCls} max-w-[140px]`} value={ruleForm.discount_percent} onChange={(e) => setRule('discount_percent', e.target.value)} />
                  <span className="text-sm text-gray-500">% en precio de venta</span>
                </div>
              </div>
            )}
            {ruleForm.price_type === 'FORMULA' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fórmula</label>
                <input className={inputCls} value={ruleForm.formula} onChange={(e) => setRule('formula', e.target.value)} placeholder="Ej: precio * 0.8" />
              </div>
            )}
            {ruleForm.price_type === 'FIXED' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Precio fijo</label>
                <input type="number" step="0.01" min="0" className={inputCls} value={ruleForm.fixed_price} onChange={(e) => setRule('fixed_price', e.target.value)} />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad mínima</label>
                <input type="number" step="0.01" min="0" className={inputCls} value={ruleForm.min_quantity} onChange={(e) => setRule('min_quantity', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Validez</label>
                <div className="flex items-center gap-2">
                  <input type="date" className={inputCls} value={ruleForm.valid_from} onChange={(e) => setRule('valid_from', e.target.value)} />
                  <span className="text-gray-400">–</span>
                  <input type="date" className={inputCls} value={ruleForm.valid_to} onChange={(e) => setRule('valid_to', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-800">
              En la línea de la orden, el precio original es el precio unitario y el descuento queda en la columna Descuento.
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={ruleForm.is_active} onChange={(e) => setRule('is_active', e.target.checked)} />
                Activa
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowRuleForm(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700">Descartar</button>
                <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-60">
                  <Save className="w-4 h-4" />
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default DiscountManager;
